import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { Box } from 'entities/Box';
import { WarehouseForBooking } from 'entities/Warehouse';
import React, { FC, memo, ReactNode, useCallback, useEffect } from 'react';
import { InfoTag } from 'shared/ui/InfoTag';
import { bookingHeaderActions, BookingSteps, getActiveStep, getCompletedSteps } from 'widgets/BookingHeader';
import { AppRoutes } from 'app/config/routerConfig/types';
import { ModernSelectBoxSizeForm } from 'widgets/ModernSelectBoxSizeForm';
import { SelectWarehouseMapUnified } from 'widgets/SelectWarehouseMapUnified';
import { ModernUserDetailsStep } from 'widgets/ModernUserDetailsForm';
import { Button } from 'shared/ui/Button';
import { useScrollToElement } from 'shared/utils/hooks/useScrollToElement';
import { contractOptionsActions, getContractOptions, UnitType } from 'entities/Contract';
import { boxSizeFilterActions } from 'features/BoxSizeFilter';
import { getBoxInfo } from '../model/selectors/getBoxInfo';
import { getWarehouseInfo } from '../model/selectors/getWarehouseInfo';
import { bookingPageActions } from '../model/slice/bookingPageSlice';
import { ModernContractInformationForm, ModernSummaryList } from 'widgets/ModernContractInformationForm';
import { ModernPaymentStep } from 'widgets/ModernPaymentForm';
import { useSetBookingDataFromQuery } from '../utils/hooks/useSetBookingDataFromQuery';
import { getLoggedUserData } from 'entities/User';
import { setBookingDataToLocalStorage } from '../utils/helpers/setBookingDataToLocalStorage';
import { globalConfirmModalActions } from 'app/globalConfirmModal';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { STARTED_BOOKING_DATA_KEY } from '../utils/constants';
import { useQueryString } from 'shared/utils/helpers/convertQueryString';
import { PriceDetailsTable } from 'features/PriceDetailsTable';
import { GratitudeModalType, useOpenGratitudeModal, GratitudeModal } from 'features/GratitudeModal';
import { ReserveBox } from 'features/ReserveBox';
import { useNavigate } from 'react-router-dom';

export const BookingPage: FC = memo(() => {
  const { t } = useAppTranslation('booking');
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const bookingStep = useAppSelector(getActiveStep);
  const completedSteps = useAppSelector(getCompletedSteps);
  const warehouseInfo = useAppSelector(getWarehouseInfo);
  const boxInfo = useAppSelector(getBoxInfo);
  const contractOptions = useAppSelector(getContractOptions);
  const loggedUser = useAppSelector(getLoggedUserData);

  const { scrollToTop } = useScrollToElement();
  const { openGratitudeModal } = useOpenGratitudeModal();

  useSetBookingDataFromQuery();

  const {
    parsedQuery: { facilityId: warehouseId },
  } = useQueryString();

  const closeConfirmModal = useCallback((): void => {
    dispatch(globalConfirmModalActions.setGlobalConfirmModalData(null));

    localStorage.removeItem(STARTED_BOOKING_DATA_KEY);
  }, [dispatch]);

  const openConfirmModal = useCallback((): void => {
    dispatch(
      globalConfirmModalActions.setGlobalConfirmModalData({
        title: t('It looks like you started a booking!'),
        description: t('Would you like to pick up where you left off?'),
        onCancel: closeConfirmModal,
        onOk: () => {
          const storedQuery = localStorage.getItem(STARTED_BOOKING_DATA_KEY);
          if (storedQuery) {
            const newUrl = `${window.location.pathname}?${storedQuery}`;
            window.location.assign(newUrl);
          }
        },
      }),
    );
  }, [closeConfirmModal, dispatch, t]);

  useEffect(() => {
    const startedBookingData = localStorage.getItem(STARTED_BOOKING_DATA_KEY);

    if (startedBookingData && !warehouseInfo && !warehouseId) {
      openConfirmModal();
    }
  }, [bookingStep, completedSteps, openConfirmModal, warehouseId, warehouseInfo]);

  useEffect(() => {
    scrollToTop();
  }, [bookingStep, scrollToTop]);

  useEffect(() => {
    return () => {
      dispatch(bookingPageActions.resetAllBookingInfo());
      dispatch(bookingHeaderActions.resetAllBookingData());
    };
  }, [dispatch]);

  const selectWarehouse = useCallback(
    (warehouse: WarehouseForBooking): void => {
      const isNewWarehouseSelected = warehouseInfo?.warehouseId !== warehouse.warehouseId;

      if (isNewWarehouseSelected) {
        dispatch(
          contractOptionsActions.setContractOptions({
            insuranceId: null,
            insuranceAmountWithVat: 0,
            insuranceAmountWithoutVat: 0,
            insuranceVatRateAmount: 0,
            coverageAmount: null,
          }),
        );
        dispatch(bookingHeaderActions.clearBookingProgressData());
        dispatch(bookingPageActions.clearInfoAfterWarehouseChange());
        dispatch(boxSizeFilterActions.resetFilter());
      }

      dispatch(bookingPageActions.setWarehouseInfo(warehouse));
      dispatch(bookingHeaderActions.changeActiveStep(BookingSteps.SELECT_UNIT));

      if (isNewWarehouseSelected) {
        setBookingDataToLocalStorage({ facilityId: warehouse.warehouseId, sizeCodeId: null, boxId: null }, navigate);
      } else {
        setBookingDataToLocalStorage({ facilityId: warehouse.warehouseId }, navigate);
      }
    },
    [dispatch, navigate, warehouseInfo?.warehouseId],
  );

  const selectBox = useCallback(
    (box: Box, sizeCodeId?: string): void => {
      const isNewBoxSelected = boxInfo?.boxId !== box.boxId;

      if (isNewBoxSelected) {
        dispatch(
          contractOptionsActions.setContractOptions({
            promoCodeValue: null,
          }),
        );
      }

      dispatch(bookingPageActions.setBoxInfo({ selectedBox: box, selectedSizeCodeId: sizeCodeId }));

      setBookingDataToLocalStorage({ sizeCodeId: sizeCodeId || null, boxId: box.boxId }, navigate);
    },
    [boxInfo?.boxId, dispatch, navigate],
  );

  const bookBox = useCallback(
    (box: Box, sizeCodeId?: string) => {
      const isNewBoxSelected = boxInfo?.boxId !== box.boxId;

      if (isNewBoxSelected) {
        dispatch(
          contractOptionsActions.setContractOptions({
            promoCodeValue: null,
          }),
        );
      }

      dispatch(bookingPageActions.setBoxInfo({ selectedBox: box, selectedSizeCodeId: sizeCodeId }));
      dispatch(bookingHeaderActions.changeActiveStep(BookingSteps.CONTRACT_INFORMATION));

      setBookingDataToLocalStorage({ sizeCodeId: sizeCodeId || null, boxId: box.boxId }, navigate);
    },
    [boxInfo?.boxId, dispatch, navigate],
  );

  const completeContractInfoAuthorized = useCallback((): void => {
    dispatch(bookingHeaderActions.changeActiveStep(BookingSteps.PAYMENT));
  }, [dispatch]);

  const completeContractInfoUnAuthorized = useCallback((): void => {
    dispatch(bookingHeaderActions.changeActiveStep(BookingSteps.YOUR_DETAILS));
  }, [dispatch]);

  const onRentOptionsChange = useCallback(() => {
    dispatch(bookingHeaderActions.changeActiveStep(BookingSteps.CONTRACT_INFORMATION));
  }, [dispatch]);

  const goToPaymentForm = useCallback(() => {
    dispatch(bookingHeaderActions.changeActiveStep(BookingSteps.PAYMENT));
  }, [dispatch]);

  const bookAndPay = useCallback((): void => {
    localStorage.removeItem(STARTED_BOOKING_DATA_KEY);
  }, []);

  const handleOpenGratitudeModal = useCallback((): void => {
    openGratitudeModal({
      description: t(
        'Our company values each and every customer. If you have any questions or feedback, please don`t hesitate to reach out!',
      ),
      type: GratitudeModalType.RESERVATION,
      buttonId: 'reservation id', // TODO ADD ID FROM MARCUS !!!
    });
  }, [openGratitudeModal, t]);

  const onReservationSuccess = useCallback(() => {
    localStorage.removeItem(STARTED_BOOKING_DATA_KEY);

    navigate(AppRoutes.MY_BOXES);

    handleOpenGratitudeModal();
  }, [handleOpenGratitudeModal, navigate]);

  const renderStepScreen = useCallback((): Nullable<ReactNode> => {
    switch (bookingStep) {
      case BookingSteps.SELECT_LOCATION:
        return <SelectWarehouseMapUnified onCompleteStep={selectWarehouse} />;
      case BookingSteps.SELECT_UNIT:
        return (
          <ModernSelectBoxSizeForm
            selectedBoxId={boxInfo?.boxId}
            selectedWarehouseId={warehouseInfo?.warehouseId}
            warehouseInfo={warehouseInfo}
            onSelectBox={selectBox}
            onSelectWarehouse={selectWarehouse}
            onCompleteStep={bookBox}
          />
        );
      case BookingSteps.CONTRACT_INFORMATION:
        return (
          <ModernContractInformationForm
            onCompleteStepAuthorized={completeContractInfoAuthorized}
            onCompleteStepUnAuthorized={completeContractInfoUnAuthorized}
            selectedWarehouseId={warehouseInfo?.warehouseId}
          />
        );
      case BookingSteps.YOUR_DETAILS:
        return <ModernUserDetailsStep onCompleteStep={goToPaymentForm} onRentOptionsChange={onRentOptionsChange} />;
      case BookingSteps.PAYMENT:
        if (boxInfo?.unitType === UnitType.OFFICE) {
          return (
            <div className="flex gap-6">
              <div className="flex-1">
                <div className="bg-secondaryLight rounded-lg p-6">
                  <PriceDetailsTable boxInfo={boxInfo} contractInfo={contractOptions} />

                  <div className="flex flex-col mt-2">
                    <InfoTag className="w-full">{t('Administrator will contact you to provide instructions')}</InfoTag>
                    <ReserveBox
                      action={
                        <Button containerClassName="w-full mt-2" size="medium" theme="primary">
                          {t('Reserve')}
                        </Button>
                      }
                      boxId={boxInfo?.boxId}
                      contractOptions={contractOptions}
                      warehouseId={warehouseInfo?.warehouseId}
                      onSuccess={onReservationSuccess}
                      userId={loggedUser?.userId}
                    />
                  </div>
                </div>
              </div>

              <div className="w-[350px] flex-shrink-0">
                <ModernSummaryList warehouseInfo={warehouseInfo} boxInfo={boxInfo} contractOptions={contractOptions} />
              </div>
            </div>
          );
        }

        return (
          <ModernPaymentStep boxInfo={boxInfo} warehouseInfo={warehouseInfo} contractInfo={contractOptions} onCompleteStep={bookAndPay} />
        );
      default:
        return null;
    }
  }, [
    bookingStep,
    selectWarehouse,
    boxInfo,
    warehouseInfo,
    selectBox,
    bookBox,
    completeContractInfoAuthorized,
    completeContractInfoUnAuthorized,
    contractOptions,
    goToPaymentForm,
    onRentOptionsChange,
    bookAndPay,
    t,
    onReservationSuccess,
    loggedUser?.userId,
  ]);

  return (
    <div className="container mx-auto px-4 desktop:py-8">
      {renderStepScreen()}
      {/* <GratitudeModal /> */}
    </div>
  );
});
